import { HTMLStencilElement } from "@stencil/core/internal";
import { breakpoint } from "@porsche-design-system/components-js/styles";
import windowService from "../services/window-service";
import { Timeout } from "./timeout";

export async function componentsOnReady(
  selector: HTMLElement | [HTMLElement, ...string[]],
  timeout: Timeout,
  shouldWaitForAnimation = windowService.getBreakpoint() >= breakpoint.s,
): Promise<[...HTMLElement[], unknown] | null> {
  if (selector === null) {
    return null;
  }
  const isArray = Array.isArray(selector);
  const parent = isArray ? selector?.[0] : selector;
  const waitForElement = async (query?: string): Promise<HTMLElement> => {
    const el = await windowService.waitForElement(parent, query);

    if (typeof (el as HTMLStencilElement)?.componentOnReady === "function") {
      await (el as HTMLStencilElement).componentOnReady();
    }

    return el;
  };

  const waitForElements = isArray
    ? (selector.slice(1) as string[]).map(waitForElement)
    : [await waitForElement()];

  const timer = new Promise<null>((resolve) =>
    timeout.set(() => resolve(null)),
  );
  let eventListener: EventListenerOrEventListenerObject;
  return Promise.race([
    Promise.all([
      ...waitForElements,
      shouldWaitForAnimation &&
        new Promise((resolve) => {
          eventListener = waitForAnimation(resolve);
          window.addEventListener("finishedAnimation", eventListener);
        }).finally(() =>
          window.removeEventListener("finishedAnimation", eventListener),
        ),
    ]),
    timer,
  ]);
}

function waitForAnimation(resolve: (T: unknown) => void) {
  return () => resolve(null);
}
